import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { SelectType, WorkSortOrder, TextAlignment, DisplayAlignment, DisplaySize, WorkType, CountryType, GroupMemberRole, SortDirection, SizeUnit, SizeRatio, SystemAlertType, CoverHeight, TextBlockType, PerPage, Language, ExposureLevel, ProjectType, ProjectInfoType, SupportType, SupportArea, PEInfoType } from '../enum/EnumRepository';


const selectOptions = {
  [SelectType.TEXT_ALIGNMENT]: Object.values(TextAlignment),
  [SelectType.DISPLAY_ALIGNMENT]: Object.values(DisplayAlignment),
  [SelectType.PE_INFO_TYPE]: Object.values(PEInfoType),
  [SelectType.PROJECT_INFO_TYPE]: Object.values(ProjectInfoType),
  [SelectType.WORK_SIZE]: Object.values(DisplaySize),
  [SelectType.SORT_ORDER]: Object.values(WorkSortOrder),
  [SelectType.SORT_DIRECTION]: Object.values(SortDirection),
  [SelectType.WORK_TYPE]: Object.values(WorkType),
  [SelectType.COUNTRY]: Object.values(CountryType),
  [SelectType.GROUP_MEMBER_ROLE]: Object.values(GroupMemberRole),
  [SelectType.SIZE_UNIT]: Object.values(SizeUnit),
  [SelectType.SIZE_RATIO]: Object.values(SizeRatio),  
  [SelectType.SYSTEM_ALERT_TYPE]: Object.values(SystemAlertType),
  [SelectType.COVER_HEIGHT]: Object.values(CoverHeight),
  [SelectType.PROJECT_TYPE]: Object.values(ProjectType),
  [SelectType.TEXT_BLOCK_TYPE]: Object.values(TextBlockType),
  [SelectType.PER_PAGE]: Object.values(PerPage),
  [SelectType.LANGUAGE]: Object.values(Language),
  [SelectType.EXPOSURE_LEVEL]: Object.values(ExposureLevel),
  [SelectType.SUPPORT_TYPE]: Object.values(SupportType),
  [SelectType.SUPPORT_AREA]: Object.values(SupportArea),
};

type SelectValue = TextAlignment |
  DisplayAlignment |
  DisplaySize |
  PEInfoType |
  ProjectInfoType |
  WorkSortOrder | SortDirection |
  WorkType |
  CoverHeight |
  ProjectType |
  CountryType |
  GroupMemberRole |
  SizeUnit |
  SizeRatio |
  SystemAlertType |
  TextBlockType |
  PerPage |
  Language |
  ExposureLevel |
  SupportType |
  SupportArea;

type SelectBoxValue = SelectValue | string | number;

/** 페이지 크롬(섬·리스트 버튼 4). 태그(3)보다 위, 펼친 네비(5)·햄버거(6)·모달(10)보다 아래. */
const SELECT_MENU_Z_PAGE = 4;
/** ModalTemplate 오버레이(10)와 동일. modal-root에 붙이면 오버레이보다 뒤에 그려져 목록이 보임. */
const SELECT_MENU_Z_MODAL = 10;

const getSelectMenuLayer = () => {
  const modalRoot = document.getElementById('modal-root');
  if (modalRoot && modalRoot.childElementCount > 0) {
    return { parent: modalRoot, zIndex: SELECT_MENU_Z_MODAL };
  }
  return { parent: document.body, zIndex: SELECT_MENU_Z_PAGE };
};

const readTriggerType = (el: HTMLElement) => {
  const s = window.getComputedStyle(el);
  return {
    fontSize: s.fontSize,
    fontWeight: s.fontWeight,
    fontFamily: s.fontFamily,
    color: s.color,
    letterSpacing: s.letterSpacing,
    lineHeight: s.lineHeight,
    textTransform: s.textTransform,
  };
};

interface SelectBoxProps<T extends SelectBoxValue> {
  value: T;
  selectType?: SelectType;
  options?: readonly T[];
  handleChange: (value: T) => void;
  direction: boolean; //true: UP, false: DOWN
  getDisplayText?: (value: T) => string;
  getOptionDisplayText?: (value: T) => string;
  isOptionDisabled?: (value: T) => boolean;
}


const SelectBox = <T extends SelectBoxValue>({
  value,
  selectType,
  options: customOptions,
  handleChange,
  direction,
  getDisplayText,
  getOptionDisplayText,
  isOptionDisabled,
}: SelectBoxProps<T>): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
    zIndex: number;
    parent: HTMLElement;
    type: ReturnType<typeof readTriggerType>;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  const options = customOptions
    ?? (selectType ? selectOptions[selectType] as unknown as readonly T[] : []);

  const getOptionLabel = (option: T) =>
    getOptionDisplayText?.(option) ?? (getDisplayText ? getDisplayText(option) : String(option));

  const updateMenuPos = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = direction || (spaceBelow < 160 && spaceAbove > spaceBelow);
    const maxHeight = Math.max(80, (openUp ? spaceAbove : spaceBelow) - 8);
    const { parent, zIndex } = getSelectMenuLayer();
    setMenuPos({
      left: rect.left,
      width: rect.width,
      maxHeight,
      zIndex,
      parent,
      type: readTriggerType(el),
      ...(openUp
        ? { bottom: window.innerHeight - rect.top }
        : { top: rect.bottom }),
    });
  }, [direction]);

  useEffect(() => {
    if (!isOpen) {
      setMenuPos(null);
      return;
    }
    updateMenuPos();
    const handler = () => updateMenuPos();
    window.addEventListener('resize', handler);
    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('scroll', handler, true);
    };
  }, [isOpen, updateMenuPos]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (optionsRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <SelectContainer ref={containerRef}>
      <SelectButton onClick={() => {
        if (isOpen) {
          setIsOpen(false);
          return;
        }
        updateMenuPos();
        setIsOpen(true);
      }}>
        <span>{getDisplayText ? getDisplayText(value) : value}</span>
        <SelectDirection $isOpen={isOpen}>{direction ? '▲' : '▼'}</SelectDirection>
      </SelectButton>
      {isOpen && menuPos && createPortal(
        <OptionsContainer
          ref={optionsRef}
          style={{
            left: menuPos.left,
            width: menuPos.width,
            maxHeight: menuPos.maxHeight,
            zIndex: menuPos.zIndex,
            ...menuPos.type,
            ...(menuPos.top != null ? { top: menuPos.top } : { bottom: menuPos.bottom }),
          }}
        >
          {options.map((option) => {
            const disabled = isOptionDisabled?.(option) ?? false;
            return (
              <Option
                key={option}
                $isSelected={option === value}
                $isDisabled={disabled}
                onClick={() => {
                  if (disabled) return;
                  handleChange(option);
                  setIsOpen(false);
                }}
              >
                {getOptionLabel(option)}
              </Option>
            );
          })}
        </OptionsContainer>,
        menuPos.parent,
      )}
    </SelectContainer>
  );
};


const SelectContainer = styled.div`
  position: relative;
  width: 100%;
  min-width: 30px;
`;

const SelectButton = styled.div`
  width: 100%;
  padding: 1px;
  background-color: transparent;
  border-bottom: 0.5px solid ${({ theme }) => theme.colors.color_Gray_04};
  border-radius: 1px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  
  &:focus {
    outline: none;
    }
`;

const SelectDirection = styled.span<{ $isOpen: boolean }>`
  transform: ${({ $isOpen }) => ($isOpen ? "scaleY(-1) scale(0.6)" : "scaleY(1) scale(0.6)")};
  transition: transform 0.6s ease-in-out;
  opacity: 0.8;
`;

const OptionsContainer = styled.div`
  position: fixed;
  overflow-y: auto;
  backdrop-filter: blur(4px);
  background-color: ${({ theme }) => theme.colors.color_Alpha_04};
  border-bottom: 0.8px solid ${({ theme }) => theme.colors.color_Gray_04};
  &::-webkit-scrollbar {
    display: none;
  }
`;


const Option = styled.div<{ $isSelected: boolean; $isDisabled: boolean }>`
  padding: 2px;
  cursor: ${({ $isDisabled }) => ($isDisabled ? 'default' : 'pointer')};
  opacity: ${({ $isDisabled }) => ($isDisabled ? 0.4 : 1)};
  background-color: ${({ $isSelected }) => $isSelected ? '#f5f5f5' : 'transparent'};
  &:hover {
    background-color: ${({ $isDisabled }) => ($isDisabled ? 'transparent' : '#f5f5f5')};
  }
`;

export default SelectBox;
